import Img from '../../../components/ui/Img';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../../../config/brands';
import { BRAND_PROFILES } from '../../../config/brandProfiles';
import type { StatementModel, WorkbenchState } from '../../utils/useStatementModel';
import { centsToDollars, monthLabel } from '../../utils/useStatementModel';

interface PrintableStatementProps {
  model: StatementModel;
  state: WorkbenchState;
  month: string;
  /** Whose programs the statement covers. The letterhead does not vary; this line does. */
  brand?: BrandKey;
}

const pct = (p: number): string => `${(p * 100).toFixed(2).replace(/\.?0+$/, '')}%`;

/**
 * Print-friendly facility rent statement — same letterhead style as
 * PrintableInvoice. Renders exactly the composed statement (row order,
 * overrides, add-backs, adjustments); the printout is the frozen paid-record.
 */
export function PrintableStatement({ model, state, month, brand = DEFAULT_BRAND_KEY }: PrintableStatementProps) {
  const overridden = model.programs.filter((p) => p.overridden);

  return (
    // "printable-statement" registers with the global @media print visibility
    // whitelist in index.css (body * is hidden in print; only whitelisted
    // printable-* trees are shown).
    <div className="printable-statement relative z-20 mx-auto max-w-4xl bg-white p-8 print:p-4">
      {/* Letterhead */}
      <div className="mb-8 flex items-start justify-between print:mb-6">
        <div className="flex flex-col">
          <div className="mb-2 flex items-center gap-4">
            <Img
              src="/assets/bhcs-logo-small.png"
              alt="Bright Horizon Chinese School"
              className="h-16 w-auto"
              width={256}
              height={256}
            />
            <h2 className="text-xl font-semibold print:text-lg">Bright Horizon Chinese School</h2>
          </div>
          <p className="text-gray-600">650 Shell Blvd</p>
          <p className="text-gray-600">Foster City, CA 94404</p>
          <p className="text-gray-600">Phone: 650-620-0299</p>
          <p className="text-gray-600">Email: billing@bhcskids.com</p>
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900 print:text-xl">REVENUE SHARE STATEMENT</h1>
          <p className="text-gray-600">{monthLabel(month)}</p>
        </div>
      </div>

      {/* Statement metadata */}
      <div className="mb-6 text-right text-sm text-gray-600">
        <p>Programs: {BRAND_PROFILES[brand].name}</p>
        <p>Share: {pct(state.sharePct)}</p>
        <p>Generated: {new Date().toLocaleString()}</p>
        {overridden.length > 0 && (
          <p>Overrides: {overridden.map((p) => `${p.programName} ${pct(p.sharePct)}`).join('; ')}</p>
        )}
      </div>

      {/* Statement table */}
      <table className="mb-6 w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-800 text-left text-gray-900">
            <th className="py-2 pr-2">Program</th>
            <th className="py-2 pr-2 text-right">Payments</th>
            <th className="py-2 pr-2 text-right">Gross tuition</th>
            <th className="py-2 pr-2 text-right">Bonus excl.</th>
            <th className="py-2 pr-2 text-right">Rent base</th>
            <th className="py-2 pr-2 text-right">Share</th>
            <th className="py-2 text-right">Rent</th>
          </tr>
        </thead>
        <tbody>
          {model.programs.map((p) => (
            <tr key={p.programId} className="border-b border-gray-200 text-gray-800">
              <td className="py-1.5 pr-2">{p.programName}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{p.nPayments}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">${centsToDollars(p.grossTuitionCents)}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {p.bonusCents !== 0 ? `−$${centsToDollars(p.bonusCents)}` : '—'}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">${centsToDollars(p.baseCents)}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {pct(p.sharePct)}
                {p.overridden ? ' *' : ''}
              </td>
              <td className="py-1.5 text-right tabular-nums">${centsToDollars(p.rentCents)}</td>
            </tr>
          ))}
          {model.addBacks.map((a) => (
            <tr key={a.key} className="border-b border-gray-200 italic text-gray-600">
              <td className="py-1.5 pr-2" colSpan={4}>
                {a.label}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">+${centsToDollars(a.amountCents)}</td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{pct(state.sharePct)}</td>
              <td className="py-1.5 text-right tabular-nums">+${centsToDollars(a.rentCents)}</td>
            </tr>
          ))}
          {model.adjustments.map((a) => (
            <tr key={a.id} className="border-b border-gray-200 italic text-gray-600">
              <td className="py-1.5 pr-2" colSpan={4}>
                Adjustment: {a.label}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">
                {a.amountCents < 0 ? '−' : '+'}${centsToDollars(Math.abs(a.amountCents))}
              </td>
              <td className="py-1.5 pr-2 text-right tabular-nums">{pct(state.sharePct)}</td>
              <td className="py-1.5 text-right tabular-nums">
                {a.rentCents < 0 ? '−' : '+'}${centsToDollars(Math.abs(a.rentCents))}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-800 font-bold text-gray-900">
            <td className="py-2 pr-2">TOTAL</td>
            <td className="py-2 pr-2 text-right tabular-nums">{model.totals.nPayments}</td>
            <td className="py-2 pr-2 text-right tabular-nums">
              ${centsToDollars(model.totals.grossTuitionCents)}
            </td>
            <td className="py-2 pr-2 text-right tabular-nums">
              {model.totals.bonusExcludedCents !== 0
                ? `−$${centsToDollars(model.totals.bonusExcludedCents)}`
                : '—'}
            </td>
            <td className="py-2 pr-2 text-right tabular-nums">${centsToDollars(model.totals.baseCents)}</td>
            <td />
            <td className="py-2 text-right tabular-nums">${centsToDollars(model.totals.rentCents)}</td>
          </tr>
        </tfoot>
      </table>

      <p className="text-xs text-gray-500">
        Rent base = tuition collected in {monthLabel(month)} (cash basis, tuition-first allocation per
        invoice, discounts netted, fees &amp; materials excluded, BHCS Cash bonus excluded). Refunds
        reduce the month they are dated. * = per-program share override.
      </p>
    </div>
  );
}
